import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, ExternalLink, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/* ============================================================
 * Admin — RentFaster URL importer.
 * Paste a rentfaster.ca listing URL → backend parses their public
 * search.json API into a draft property + unit types → user reviews /
 * edits → clicks Import → backend downloads + compresses + uploads
 * every provided photo URL, then inserts the property + units into
 * Supabase. Redirect to the residence page on success.
 * ============================================================ */
export default function AdminImport() {
  const [url, setUrl] = useState(
    "https://www.rentfaster.ca/properties/10611-144-st-nw-edmonton-767196",
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [photoUrls, setPhotoUrls] = useState("");
  const [longDesc, setLongDesc] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await fetch(`${API}/api/rentfaster/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Preview failed.");
      setDraft(j);
      // Seed the photo URL textarea with the CDN hint(s).
      setPhotoUrls((j.hint_photo_urls || []).join("\n"));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    setImporting(true);
    setError("");
    try {
      const photos = photoUrls
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await fetch(`${API}/api/rentfaster/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          property: draft.property,
          units: draft.units,
          photo_urls: photos,
          long_description: longDesc,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Import failed.");
      setResult(j);
    } catch (e) {
      setError(e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#EDE9E0", color: "#201E1A" }}
      data-testid="admin-import-page"
    >
      <div className="mx-auto max-w-[1080px] px-6 py-16 md:px-10 md:py-24">
        <header className="mb-10 flex items-baseline justify-between">
          <div>
            <p
              className="label-caps text-[11px]"
              style={{ color: "#C89A4E", letterSpacing: "0.24em" }}
            >
              ROAM Admin
            </p>
            <h1
              className="font-display mt-3"
              style={{ fontSize: "clamp(36px,4.6vw,56px)", lineHeight: 1.02 }}
            >
              Import from{" "}
              <span className="italic" style={{ color: "#C89A4E" }}>
                RentFaster.
              </span>
            </h1>
            <p
              className="mt-3 max-w-[52ch] text-[15px]"
              style={{ color: "rgba(32,30,26,0.7)" }}
            >
              Paste any rentfaster.ca listing URL. We&rsquo;ll pull the address,
              community, coordinates, features, pet/smoking policies and every
              unit variant automatically — then you review and hit import.
            </p>
          </div>
          <Link
            to="/admin"
            className="label-caps text-[11px]"
            style={{ color: "#24382E" }}
          >
            ← Admin home
          </Link>
        </header>

        {/* ---------- URL input ---------- */}
        <section
          className="rounded-2xl p-6 md:p-8"
          style={{
            backgroundColor: "#F8F5EF",
            border: "1px solid rgba(32,30,26,0.08)",
          }}
        >
          <label
            className="label-caps mb-3 block text-[10px]"
            style={{ color: "rgba(32,30,26,0.65)" }}
          >
            RentFaster listing URL
          </label>
          <div className="flex gap-3">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.rentfaster.ca/properties/…"
              className="flex-1 rounded-xl border px-4 py-3 text-[14px] outline-none focus:border-[#C89A4E]"
              style={{ borderColor: "rgba(32,30,26,0.15)" }}
              data-testid="rentfaster-url-input"
            />
            <button
              type="button"
              onClick={fetchPreview}
              disabled={!url.trim() || loading}
              className="pill pill-gold inline-flex items-center gap-2 text-[14px] disabled:opacity-50"
              data-testid="rentfaster-preview-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              {loading ? "Fetching…" : "Preview"}
            </button>
          </div>
          {error && (
            <p
              className="mt-3 rounded-lg px-3 py-2 text-[13px]"
              style={{
                backgroundColor: "rgba(196,89,64,0.1)",
                color: "#8A3020",
              }}
              data-testid="import-error"
            >
              {error}
            </p>
          )}
        </section>

        {/* ---------- Preview & Edit ---------- */}
        {draft && !result && (
          <section
            className="mt-8 rounded-2xl p-6 md:p-8"
            style={{
              backgroundColor: "#F8F5EF",
              border: "1px solid rgba(32,30,26,0.08)",
            }}
            data-testid="import-preview"
          >
            <h2
              className="font-display text-[24px]"
              style={{ letterSpacing: "-0.01em" }}
            >
              Review &amp; edit
            </h2>

            {/* Property core */}
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <Field
                label="Property name"
                value={draft.property.name}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    property: { ...draft.property, name: v },
                  })
                }
                testid="import-field-name"
              />
              <Field
                label="Slug (URL)"
                value={draft.property.slug}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    property: { ...draft.property, slug: v },
                  })
                }
                testid="import-field-slug"
              />
              <Field
                label="Tagline"
                value={draft.property.tagline}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    property: { ...draft.property, tagline: v },
                  })
                }
                testid="import-field-tagline"
              />
              <Field
                label="Address"
                value={draft.property.address}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    property: { ...draft.property, address: v },
                  })
                }
                testid="import-field-address"
              />
              <Field
                label="City"
                value={draft.property.city}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    property: { ...draft.property, city: v },
                  })
                }
                testid="import-field-city"
              />
              <Field
                label="Community"
                value={draft.property.community}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    property: {
                      ...draft.property,
                      community: v,
                      community_slug: v
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)/g, ""),
                    },
                  })
                }
                testid="import-field-community"
              />
              <Field
                label="Pet policy"
                value={draft.property.pet_policy}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    property: { ...draft.property, pet_policy: v },
                  })
                }
              />
              <Field
                label="Smoking policy"
                value={draft.property.smoking_policy}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    property: { ...draft.property, smoking_policy: v },
                  })
                }
              />
            </div>

            {/* Units table */}
            <h3
              className="label-caps mt-8 text-[10px]"
              style={{ color: "rgba(32,30,26,0.65)" }}
            >
              Unit types detected · {draft.units.length}
            </h3>
            <div
              className="mt-3 overflow-hidden rounded-xl border"
              style={{ borderColor: "rgba(32,30,26,0.1)" }}
            >
              <table className="w-full text-[13px]">
                <thead style={{ backgroundColor: "rgba(32,30,26,0.04)" }}>
                  <tr>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Beds</th>
                    <th className="px-3 py-2 text-left">Baths</th>
                    <th className="px-3 py-2 text-left">Rent</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.units.map((u, i) => (
                    <tr
                      key={i}
                      style={{
                        borderTop: "1px solid rgba(32,30,26,0.06)",
                      }}
                      data-testid={`import-unit-row-${i}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          value={u.name}
                          onChange={(e) => {
                            const units = [...draft.units];
                            units[i] = { ...u, name: e.target.value };
                            setDraft({ ...draft, units });
                          }}
                          className="w-full bg-transparent outline-none"
                        />
                      </td>
                      <td className="px-3 py-2">{u.beds}</td>
                      <td className="px-3 py-2">{u.baths}</td>
                      <td className="px-3 py-2">${u.rent_from}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Photos + description */}
            <div className="mt-8 grid gap-4">
              <div>
                <label
                  className="label-caps mb-2 block text-[10px]"
                  style={{ color: "rgba(32,30,26,0.65)" }}
                >
                  Photo URLs (one per line) — will be downloaded, compressed
                  &amp; uploaded to Supabase
                </label>
                <textarea
                  value={photoUrls}
                  onChange={(e) => setPhotoUrls(e.target.value)}
                  rows={6}
                  placeholder="https://rf-images-prod-bcdn.rentfaster.ca/…/slide_….jpg"
                  className="w-full resize-y rounded-xl border p-3 font-mono text-[12px] outline-none focus:border-[#C89A4E]"
                  style={{ borderColor: "rgba(32,30,26,0.15)" }}
                  data-testid="import-photos-input"
                />
                <p
                  className="mt-2 text-[12px]"
                  style={{ color: "rgba(32,30,26,0.55)" }}
                >
                  Tip: right-click each photo on the RentFaster page →{" "}
                  <span className="italic">Copy image address</span> → paste
                  here. First URL becomes the hero image.
                </p>
              </div>
              <div>
                <label
                  className="label-caps mb-2 block text-[10px]"
                  style={{ color: "rgba(32,30,26,0.65)" }}
                >
                  Long description (optional — becomes the neighbourhood blurb)
                </label>
                <textarea
                  value={longDesc}
                  onChange={(e) => setLongDesc(e.target.value)}
                  rows={5}
                  placeholder="Copy-paste the property description from RentFaster if you want it saved…"
                  className="w-full resize-y rounded-xl border p-3 text-[13px] outline-none focus:border-[#C89A4E]"
                  style={{ borderColor: "rgba(32,30,26,0.15)" }}
                  data-testid="import-desc-input"
                />
              </div>
            </div>

            {/* commit */}
            <div className="mt-8 flex items-center justify-end gap-3">
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[13px]"
                style={{ color: "rgba(32,30,26,0.6)" }}
              >
                Open source listing <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <button
                type="button"
                onClick={doImport}
                disabled={importing}
                className="pill pill-gold inline-flex items-center gap-2 text-[14px] disabled:opacity-50"
                data-testid="import-commit-btn"
              >
                {importing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {importing ? "Importing…" : "Create property"}
              </button>
            </div>
          </section>
        )}

        {/* ---------- Success ---------- */}
        {result && (
          <section
            className="mt-8 rounded-2xl p-8 text-center"
            style={{
              backgroundColor: "#24382E",
              color: "#F8F5EF",
              border: "1px solid rgba(200,154,78,0.4)",
            }}
            data-testid="import-success"
          >
            <p
              className="label-caps text-[10px]"
              style={{ color: "#E3B268" }}
            >
              Imported successfully
            </p>
            <h2
              className="font-display mt-3"
              style={{ fontSize: "clamp(28px,3vw,40px)" }}
            >
              Property is live in the database.
            </h2>
            <p className="mt-3 text-[14px] opacity-80">
              {result.unit_count} unit types · {result.photo_count} photos
              uploaded · slug{" "}
              <code
                style={{
                  backgroundColor: "rgba(248,245,239,0.1)",
                  padding: "2px 8px",
                  borderRadius: 6,
                }}
              >
                {result.slug}
              </code>
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to={result.view_url} className="pill pill-gold text-[14px]">
                View on site
              </Link>
              <Link
                to="/admin"
                className="pill text-[14px]"
                style={{
                  border: "1px solid rgba(248,245,239,0.4)",
                  color: "#F8F5EF",
                  backgroundColor: "transparent",
                }}
              >
                Manage / edit
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span
      className="label-caps mb-1.5 block text-[10px]"
      style={{ color: "rgba(32,30,26,0.6)" }}
    >
      {label}
    </span>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border px-3 py-2 text-[13px] outline-none focus:border-[#C89A4E]"
      style={{
        borderColor: "rgba(32,30,26,0.15)",
        backgroundColor: "#FFF",
      }}
      data-testid={testid}
    />
  </label>
);
